/**
 * Secure Storage Utility
 * Provides encrypted storage for sensitive data
 */

import CryptoJS from 'crypto-js';
import logger from './logger';
import { STORAGE_KEYS } from './constants';

/**
 * Keys that must never reach production, because they are committed to this
 * repository and are therefore public.
 *
 * The second entry is the Dockerfile's ARG default. The guard below already
 * existed but only knew about the first string — so an image built without
 * explicit build-args sailed straight past it (the Dockerfile value is a
 * different string and is longer than 32 chars) and shipped a publicly-known
 * key to production. That is exactly what happened to the platform-* images
 * built on a laptop in Aug 2026; the key also appears verbatim in the
 * 2026-06-22 post-mortem.
 *
 * Listing it here converts "silently ships a public key" into "fails loudly on
 * first load", which is the only outcome that reliably gets noticed.
 */
const PUBLIC_PLACEHOLDER_KEYS = [
  'basiq360-default-key-change-in-production',
  'FbmOxBcuF9XEylaX0SzEMiR6/6kqqJHpzH3Hzv63Cy0=',
];

// SECURITY: Validate encryption key in production
if (process.env.NODE_ENV === 'production') {
  if (!process.env.REACT_APP_ENCRYPTION_KEY ||
      PUBLIC_PLACEHOLDER_KEYS.includes(process.env.REACT_APP_ENCRYPTION_KEY)) {
    throw new Error(
      'SECURITY ERROR: REACT_APP_ENCRYPTION_KEY must be set in production and cannot use a ' +
      'committed placeholder value. Build via CI so the key is injected from repo secrets ' +
      '(.github/workflows/deploy-web-platform.yml). Generate one: openssl rand -base64 32'
    );
  }

  if (process.env.REACT_APP_ENCRYPTION_KEY.length < 32) {
    throw new Error(
      `SECURITY ERROR: REACT_APP_ENCRYPTION_KEY must be at least 32 characters. ` +
      `Current length: ${process.env.REACT_APP_ENCRYPTION_KEY.length}`
    );
  }
}

// Encryption key (validated above in production)
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'basiq360-default-key-change-in-production';

class SecureStorage {
  /**
   * Encrypt data
   */
  private encrypt(data: string): string {
    try {
      return CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();
    } catch (error) {
      logger.error('Encryption error', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypt data
   */
  private decrypt(encryptedData: string): string {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      logger.error('Decryption error', error);
      throw new Error('Failed to decrypt data');
    }
  }

  /**
   * Set item in encrypted storage
   */
  setItem(key: string, value: any, encrypt: boolean = true): void {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      const storageValue = encrypt ? this.encrypt(stringValue) : stringValue;

      localStorage.setItem(key, storageValue);

      // Store encryption flag
      if (encrypt) {
        localStorage.setItem(`${key}_encrypted`, 'true');
      }
    } catch (error) {
      logger.error(`Failed to set item in storage: ${key}`, error);
      throw error;
    }
  }

  /**
   * Get item from encrypted storage
   */
  getItem(key: string, decrypt: boolean = true): any {
    try {
      const storageValue = localStorage.getItem(key);

      if (!storageValue) {
        return null;
      }

      // Check if item is encrypted
      const isEncrypted = localStorage.getItem(`${key}_encrypted`) === 'true';

      const decryptedValue = (decrypt && isEncrypted)
        ? this.decrypt(storageValue)
        : storageValue;

      // Try to parse as JSON
      try {
        return JSON.parse(decryptedValue);
      } catch {
        return decryptedValue;
      }
    } catch (error) {
      logger.error(`Failed to get item from storage: ${key}`, error);
      return null;
    }
  }

  /**
   * Remove item from storage
   */
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}_encrypted`);
    } catch (error) {
      logger.error(`Failed to remove item from storage: ${key}`, error);
    }
  }

  /**
   * Clear all storage
   */
  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      logger.error('Failed to clear storage', error);
    }
  }

  /**
   * Check if item exists
   */
  hasItem(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Get all keys
   */
  getAllKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !key.endsWith('_encrypted')) {
        keys.push(key);
      }
    }
    return keys;
  }

  /**
   * Set auth user with encryption
   */
  setAuthUser(authData: any): void {
    this.setItem(STORAGE_KEYS.AUTH_USER, authData, true);
  }

  /**
   * Get auth user
   */
  getAuthUser(): any {
    return this.getItem(STORAGE_KEYS.AUTH_USER, true);
  }

  /**
   * Set access token with encryption
   */
  setAccessToken(token: string): void {
    this.setItem(STORAGE_KEYS.ACCESS_TOKEN, token, true);
  }

  /**
   * Get access token (decrypted)
   */
  getAccessToken(): string | null {
    return this.getItem(STORAGE_KEYS.ACCESS_TOKEN, true);
  }

  /**
   * Set refresh token with encryption
   */
  setRefreshToken(token: string): void {
    this.setItem(STORAGE_KEYS.REFRESH_TOKEN, token, true);
  }

  /**
   * Get refresh token (decrypted)
   */
  getRefreshToken(): string | null {
    return this.getItem(STORAGE_KEYS.REFRESH_TOKEN, true);
  }

  /**
   * Clear all auth data
   */
  clearAuthData(): void {
    this.removeItem(STORAGE_KEYS.AUTH_USER);
    this.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    this.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    this.removeItem(STORAGE_KEYS.MENU_DATA);
  }

  /**
   * Set menu data
   */
  setMenuData(menuData: any): void {
    this.setItem(STORAGE_KEYS.MENU_DATA, menuData, false);
  }

  /**
   * Get menu data
   */
  getMenuData(): any {
    return this.getItem(STORAGE_KEYS.MENU_DATA, false);
  }

  /**
   * Get user ID from auth data
   */
  getUserId(): string | null {
    const authUser = this.getAuthUser();
    return authUser?.id || authUser?.userId || authUser?._id || null;
  }

  /**
   * Get organization ID from auth data
   */
  getOrganizationId(): string | null {
    const authUser = this.getAuthUser();
    return authUser?.organizationId || authUser?.organization?.id || authUser?.orgId || null;
  }

  /**
   * Get storage size in bytes
   */
  getStorageSize(): number {
    let size = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        if (value) {
          size += key.length + value.length;
        }
      }
    }
    return size;
  }

  /**
   * Get storage size in MB
   */
  getStorageSizeMB(): number {
    return this.getStorageSize() / (1024 * 1024);
  }
}

// Create singleton instance
const secureStorage = new SecureStorage();

export default secureStorage;
